import React from 'react';
import type { CatalogImportRequest } from '@/api/handlers/resources/catalogImports';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CatalogImportRecord } from '@/api/models';
import {
    useCatalogImportMutation,
    useCatalogImportPreviewMutation,
} from '@/api/queries';
import { noticeFor } from '@/pages/catalogImport/notices';
import { buildCatalogImportRequest } from '@/pages/catalogImport/parse';
import {
    joinedPlans,
    useCatalogImport,
    withUnreadableCells,
    type ParsedLine,
} from '@/pages/catalogImport/store';
import { importRefusalOf, type ImportSubmitState } from './importSubmit';
import {
    SUGGESTED_FIXES_CLOSED,
    answerFiguresOf,
    canConfirmSuggestedFixes,
    suggestedFixesSummaryOf,
    unreadableRowsOf,
    type SuggestedFixesState,
} from './suggestedFixes';

export type UseSuggestedFixesOptions = {
    /** A check is running: the action waits, its answer is about to change the rows. */
    isChecking: boolean;
    /** The import is running: nothing starts until it answers. */
    isImporting: boolean;
    /** Rows that need attention and are not excluded: the action is offered while any does. */
    needsAttentionCount: number;
    /** Asks the step for a plain check once the store holds the restored lines. */
    onRequestRecheck: () => void;
    /** The import answered with its record: the step stores it and goes to Result. */
    onImported: (record: CatalogImportRecord) => void;
};

export type UseSuggestedFixesResult = {
    /**
     * "Import with suggested fixes" is offered: rows need attention. It is
     * then the bar's primary action in place of the plain Import, which
     * those rows block.
     */
    canSuggestFixes: boolean;
    /** The action waits: a check or the import runs. */
    isSuggestDisabled: boolean;
    /** The dialog. */
    suggestedFixes: SuggestedFixesState;
    /**
     * Applies every default as one bulk action, checks with `importDrafts`,
     * and opens the dialog on the answer's summary.
     */
    onSuggestFixes: () => void;
    /**
     * Confirm: imports with `importDrafts`, the request the summary
     * answered. It waits while a check runs.
     */
    onConfirmSuggestedFixes: () => void;
    /**
     * "Review rows first" (or the dialog dismissed): the defaults are taken
     * back (undo, when they changed a row) and checked again, so the step is
     * as it was. Nothing while the check or the import runs.
     */
    onReviewRows: () => void;
};

/** An import refused for rows that need attention, which drafts should have answered. */
const LINES_INVALID: ImportSubmitState = {
    status: 'refused',
    notices: [noticeFor({ code: 'IMPORT_LINES_INVALID' })],
};

/**
 * "Import with suggested fixes" (bos-app#213, US-10, AC-08), which
 * `useProductsStep` composes:
 *
 * 1. **Apply.** Every default at once (the store's `applyAllFixes`, one
 *    bulk action that snapshots first when it changes a row), and the
 *    dialog opens on "Applying the suggested fixes…".
 * 2. **Check with `importDrafts`.** The store's request over the lines the
 *    plan wrote, with `importDrafts: true`. Its answer is the summary's
 *    (`suggestedFixesSummaryOf`): what each default changed, the rows it
 *    imports as drafts and in all, and the header problems that refuse it.
 *    A row with a number cell the parse could not read is sent excluded
 *    and counted as left out (`unreadableRowsOf`): the server takes
 *    numbers only, so a draft would lose the value, and the answer goes
 *    through `withUnreadableCells` like any check, so no such row counts
 *    as imported.
 *    The answer is not stored as the preview: the table keeps the plain
 *    check, and the dialog holds this one. A check that does not answer
 *    takes the defaults back at once (nothing changed: the table's check
 *    still answers the restored lines) and says so, with Try again.
 *    **Revealed problems.** A default can reveal a problem the plain check
 *    could not see: once the IMEI move clears the barcodes that told a new
 *    variant's lines apart, their prices can differ (`VARIANT_FIELDS_DIFFER`).
 *    So the defaults that answer calls for are applied too, into the same
 *    undo step (`applyAllFixes` with `against` and `join`), and the lines
 *    are checked once more; the summary is that check's, and counts both
 *    passes (`joinedPlans`). One more pass only: the second answer's
 *    problems are the summary's.
 * 3. **Confirm** imports that very request (`importDrafts: true`), so the
 *    record matches the summary. It waits while a check runs (the import
 *    would drop that check's answer). While it runs the step holds still as for
 *    any import; the record goes to `onImported`. A refusal or no answer
 *    stays in the dialog with what it said (`submit`), and a refusal that
 *    carries a fresh answer updates the summary; Confirm then tries again.
 * 4. **Review rows first** undoes the application, only when it changed a
 *    row (a no-op application pushed nothing, and undoing would take back
 *    an earlier bulk action) and its snapshot is still the latest, and asks
 *    for a plain check, so the lines,
 *    exclusions and preview are as they were. Plain Import never sends
 *    `importDrafts`.
 */
export const useSuggestedFixes = ({
    isChecking,
    isImporting,
    needsAttentionCount,
    onRequestRecheck,
    onImported,
}: UseSuggestedFixesOptions): UseSuggestedFixesResult => {
    const { state, actions } = useCatalogImport();
    const { mutateAsync: requestPreview } = useCatalogImportPreviewMutation();
    const { mutateAsync: requestImport } = useCatalogImportMutation();

    const [dialog, setDialog] = React.useState<SuggestedFixesState>(
        SUGGESTED_FIXES_CLOSED,
    );
    // The application the open dialog stands on: whether it pushed a
    // snapshot, how deep the undo stack was with it on top, and the request
    // its summary answered.
    const applied = React.useRef<{
        changed: boolean;
        depth: number;
        request: CatalogImportRequest;
    } | null>(null);
    const isBusy = React.useRef(false);
    const isMounted = React.useRef(true);
    React.useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const onSuggestFixes = React.useCallback(() => {
        if (
            isBusy.current ||
            isChecking ||
            isImporting ||
            state.preview === null
        ) {
            return;
        }
        isBusy.current = true;
        setDialog({ status: 'applying' });
        const exclusions = state.exclusions;
        const requestFor = (lines: ParsedLine[]): CatalogImportRequest => ({
            ...buildCatalogImportRequest({
                branchId: state.branch?.id ?? '',
                file: state.file,
                lines,
                decisions: state.decisions,
                exclusions: new Set([
                    ...exclusions,
                    ...unreadableRowsOf(lines, exclusions),
                ]),
            }),
            importDrafts: true,
        });
        let plan = actions.applyAllFixes();
        let changed = plan.changedRows.length > 0;
        let depth = state.undoStack.length + (changed ? 1 : 0);
        let request = requestFor(plan.lines);
        applied.current = { changed, depth, request };

        void (async () => {
            try {
                let answer = await requestPreview(request);
                // The defaults the move revealed, in the same undo step.
                const revealed = actions.applyAllFixes({
                    against: answer,
                    join: changed,
                });
                if (revealed.changedRows.length > 0) {
                    depth += changed ? 0 : 1;
                    changed = true;
                    plan = joinedPlans(plan, revealed);
                    request = requestFor(plan.lines);
                    applied.current = { changed, depth, request };
                    answer = await requestPreview(request);
                }
                if (isMounted.current) {
                    setDialog({
                        status: 'ready',
                        summary: suggestedFixesSummaryOf({
                            plan,
                            answer: withUnreadableCells(answer, plan.lines),
                            lines: plan.lines,
                            exclusions,
                        }),
                        submit: { status: 'idle' },
                    });
                }
            } catch {
                // Nothing changed: the defaults go back, and the table's
                // check still answers the lines they restore.
                if (changed) {
                    actions.undo();
                }
                applied.current = null;
                if (isMounted.current) {
                    setDialog({ status: 'failed' });
                }
            } finally {
                isBusy.current = false;
            }
        })();
    }, [
        actions,
        isChecking,
        isImporting,
        requestPreview,
        state.branch,
        state.decisions,
        state.exclusions,
        state.file,
        state.preview,
        state.undoStack.length,
    ]);

    const onConfirmSuggestedFixes = React.useCallback(() => {
        const current = applied.current;
        if (
            isBusy.current ||
            isChecking ||
            isImporting ||
            current === null ||
            dialog.status !== 'ready' ||
            !canConfirmSuggestedFixes(dialog.summary)
        ) {
            return;
        }
        isBusy.current = true;
        setDialog({ ...dialog, submit: { status: 'idle' } });
        actions.importStarted();
        const exclusions = state.exclusions;

        void (async () => {
            try {
                const record = await requestImport(current.request);
                applied.current = null;
                onImported(record);
            } catch (error) {
                const refusal = importRefusalOf(
                    error as ErrorResponse,
                    exclusions,
                );
                actions.importFailed(error);
                if (isMounted.current) {
                    setDialog((shown) =>
                        shown.status !== 'ready'
                            ? shown
                            : {
                                  status: 'ready',
                                  summary:
                                      refusal.preview === null
                                          ? shown.summary
                                          : {
                                                ...shown.summary,
                                                ...answerFiguresOf(
                                                    refusal.preview,
                                                    exclusions,
                                                ),
                                            },
                                  submit:
                                      refusal.submit.status === 'idle'
                                          ? LINES_INVALID
                                          : refusal.submit,
                              },
                    );
                }
            } finally {
                isBusy.current = false;
            }
        })();
    }, [
        actions,
        dialog,
        isChecking,
        isImporting,
        onImported,
        requestImport,
        state.exclusions,
    ]);

    const onReviewRows = React.useCallback(() => {
        if (isBusy.current || isImporting || dialog.status === 'applying') {
            return;
        }
        const current = applied.current;
        applied.current = null;
        setDialog(SUGGESTED_FIXES_CLOSED);
        // Its own step only: one still on top of the stack.
        if (current?.changed && state.undoStack.length === current.depth) {
            actions.undo();
            onRequestRecheck();
        }
    }, [
        actions,
        dialog.status,
        isImporting,
        onRequestRecheck,
        state.undoStack.length,
    ]);

    return {
        canSuggestFixes: needsAttentionCount > 0,
        isSuggestDisabled: isChecking || isImporting,
        suggestedFixes: dialog,
        onSuggestFixes,
        onConfirmSuggestedFixes,
        onReviewRows,
    };
};
